import { makeDefaultPorts, normalizePorts, type PortParentType } from '../../utils/ports';
import { formatEntityId, formatPortId, prefixForEquipment } from '../../utils/entityIds';
import type { EquipmentInstance, EquipmentObjectType, EquipmentPort, EquipmentTemplate } from './equipmentTypes';

const SEQUENCED_TYPES = new Set<EquipmentObjectType>(['equipment', 'stocker']);

/** Display labels stay un-hyphenated: EQ1 / ST1 */
const SEQUENCE_RE = /^(EQ|ST)(\d+)$/i;

function prefixForType(objectType: EquipmentObjectType): 'EQ' | 'ST' | null {
  if (objectType === 'equipment') return 'EQ';
  if (objectType === 'stocker') return 'ST';
  return null;
}

export function parseSequencedName(name: string): { prefix: string; sequence: number } | null {
  const match = name.trim().match(SEQUENCE_RE);
  if (!match) return null;
  return { prefix: match[1].toUpperCase(), sequence: Number(match[2]) };
}

/** Next sequence number for EQ/ST among instances (+ optional extra ids e.g. layout ST-*). */
export function nextInstanceSequence(
  instances: Record<string, EquipmentInstance>,
  objectType: EquipmentObjectType,
  extraIds: Iterable<string> = []
): number {
  const prefix = prefixForType(objectType);
  if (!prefix) return 1;

  let max = 0;
  const considerId = (id: string) => {
    const idMatch = id.match(new RegExp(`^${prefix}-(\\d+)$`, 'i'));
    if (!idMatch) return;
    const n = Number(idMatch[1]);
    if (Number.isFinite(n) && n > max) max = n;
  };

  for (const inst of Object.values(instances)) {
    if (inst.objectType !== objectType) continue;
    const parsed = parseSequencedName(inst.name);
    if (parsed?.prefix === prefix && parsed.sequence > max) {
      max = parsed.sequence;
    }
    considerId(inst.id);
  }
  for (const id of extraIds) {
    considerId(id);
  }
  return max + 1;
}

export function formatInstanceLabel(objectType: EquipmentObjectType, sequence: number): string {
  const prefix = prefixForType(objectType);
  return prefix ? `${prefix}${sequence}` : `Equipment ${sequence}`;
}

/** Port id from parent instance id: EQ-1-LP-1 */
export function formatLoadPortId(parentId: string, portIndex: number): string {
  return formatPortId(parentId, portIndex);
}

function resolvePortsForInstance(
  instance: EquipmentInstance,
  templatePorts: EquipmentPort[] = []
): EquipmentPort[] {
  const parentType: PortParentType =
    instance.objectType === 'stocker' ? 'stocker' : 'equipment';
  if (instance.overrides.ports?.length) {
    return normalizePorts(
      instance.id,
      parentType,
      JSON.parse(JSON.stringify(instance.overrides.ports)) as EquipmentPort[]
    );
  }
  if (templatePorts.length) {
    return normalizePorts(
      instance.id,
      parentType,
      JSON.parse(JSON.stringify(templatePorts)) as EquipmentPort[]
    );
  }
  return makeDefaultPorts({
    parentId: instance.id,
    parentType,
  });
}

/**
 * Assign EQ1/ST1 display names, EQ-1/ST-1 typed ids, and EQ-1-LP-1 port ids.
 */
export function assignInstanceIdentity(
  instance: EquipmentInstance,
  sequence: number,
  templatePorts: EquipmentPort[] = []
): EquipmentInstance {
  const prefix = prefixForType(instance.objectType);
  if (!prefix) return instance;

  const label = formatInstanceLabel(instance.objectType, sequence);
  const typedId = formatEntityId(prefixForEquipment(instance.objectType), sequence);
  const parentType: PortParentType = instance.objectType === 'stocker' ? 'stocker' : 'equipment';
  const withId: EquipmentInstance = { ...instance, id: typedId, name: label };
  // Port ids are re-derived from the instance id so they stay unique, but the
  // labels are user-editable and are carried over untouched.
  const ports: EquipmentPort[] = resolvePortsForInstance(withId, templatePorts).map(
    (port, index) => ({
      ...port,
      id: formatLoadPortId(typedId, index + 1),
      name: String(port.name ?? '').trim() || `LP-${index + 1}`,
      parentId: typedId,
      parentType,
    })
  );

  return {
    ...withId,
    overrides: {
      ...withId.overrides,
      ports,
    },
  };
}

export type NormalizeSequencedResult = {
  instances: Record<string, EquipmentInstance>;
  instanceOrder: string[];
};

/** Re-number all EQ/ST instances in order; rewrite ids, names, and port ids. */
export function normalizeSequencedInstances(
  instances: Record<string, EquipmentInstance>,
  instanceOrder: string[],
  templates: Record<string, EquipmentTemplate>
): NormalizeSequencedResult {
  const next: Record<string, EquipmentInstance> = {};
  const nextOrder: string[] = [];
  const counters: Partial<Record<EquipmentObjectType, number>> = {};

  for (const id of instanceOrder) {
    const inst = instances[id];
    if (!inst) continue;

    if (!SEQUENCED_TYPES.has(inst.objectType)) {
      next[id] = inst;
      nextOrder.push(id);
      continue;
    }

    counters[inst.objectType] = (counters[inst.objectType] ?? 0) + 1;
    const templatePorts =
      inst.templateId && templates[inst.templateId] ? templates[inst.templateId].ports : [];

    const assigned = assignInstanceIdentity(inst, counters[inst.objectType]!, templatePorts);
    next[assigned.id] = assigned;
    nextOrder.push(assigned.id);
  }

  // Preserve any instances missing from order.
  const orderedOldIds = new Set(instanceOrder);
  for (const [id, inst] of Object.entries(instances)) {
    if (orderedOldIds.has(id)) continue;
    if (!SEQUENCED_TYPES.has(inst.objectType)) {
      next[id] = inst;
      nextOrder.push(id);
      continue;
    }
    counters[inst.objectType] = (counters[inst.objectType] ?? 0) + 1;
    const templatePorts =
      inst.templateId && templates[inst.templateId] ? templates[inst.templateId].ports : [];
    const assigned = assignInstanceIdentity(inst, counters[inst.objectType]!, templatePorts);
    next[assigned.id] = assigned;
    nextOrder.push(assigned.id);
  }

  return { instances: next, instanceOrder: nextOrder };
}

export function shouldUseSequencedIdentity(objectType: EquipmentObjectType): boolean {
  return SEQUENCED_TYPES.has(objectType);
}

export function cloneEquipmentInstance(
  src: EquipmentInstance,
  instances: Record<string, EquipmentInstance>,
  templatePorts: EquipmentPort[],
  params: {
    id?: string;
    polygon: EquipmentInstance['polygon'];
    createdAt: string;
    updatedAt: string;
  }
): EquipmentInstance {
  const sequence = nextInstanceSequence(instances, src.objectType);
  const typedId =
    params.id ??
    (shouldUseSequencedIdentity(src.objectType)
      ? formatEntityId(prefixForEquipment(src.objectType), sequence)
      : formatEntityId(prefixForEquipment(src.objectType), sequence));

  const raw: EquipmentInstance = {
    ...JSON.parse(JSON.stringify(src)),
    id: typedId,
    polygon: params.polygon,
    createdAt: params.createdAt,
    updatedAt: params.updatedAt,
  };

  if (!shouldUseSequencedIdentity(raw.objectType)) {
    return raw;
  }

  return assignInstanceIdentity(raw, sequence, templatePorts);
}
